type Pose =
  | { id?: number | string; imageRecordType?: string }
  | null
  | undefined;
type Child = { poses?: Pose[] } | null | undefined;
type Line = {
  poses?: Pose[];
  children?: Child[];
  multiConfigChildProducts?: Child[];
};
type Order = { cartItems?: Line[] } | null | undefined;

// Stamps every pose still without an imageRecordType from the server's list of
// each order's group photos (the abandoned-cart restore sends it): a listed
// photo is a group photo, any other one an individual photo. Poses already
// stamped from their product's image nodes keep their stamp. Returns the same
// orders object when nothing changes.
export const stampPosesFromGroupPhotoIds = <T extends Record<string, Order>>(
  orders: T,
  groupPhotoIds: Record<string, (number | string)[]> | null | undefined,
): T => {
  let changed = false;

  const stampOrder = (order: Order, groupIds: Set<string>): Order => {
    if (!order || !Array.isArray(order.cartItems)) return order;

    const stampPoses = (poses: Pose[] | undefined): Pose[] | undefined => {
      if (!Array.isArray(poses)) return poses;
      let posesChanged = false;
      const stamped = poses.map((pose) => {
        if (!pose || pose.imageRecordType || pose.id == null) return pose;
        posesChanged = true;
        return {
          ...pose,
          imageRecordType: groupIds.has(String(pose.id))
            ? 'group'
            : 'individual',
        };
      });
      if (!posesChanged) return poses;
      changed = true;
      return stamped;
    };
    const stampChildren = (children: Child[] | undefined) =>
      Array.isArray(children)
        ? children.map((child) =>
            child?.poses ? { ...child, poses: stampPoses(child.poses) } : child,
          )
        : children;

    return {
      ...order,
      cartItems: order.cartItems.map((line) => ({
        ...line,
        ...(line.poses ? { poses: stampPoses(line.poses) } : {}),
        ...(line.children ? { children: stampChildren(line.children) } : {}),
        ...(line.multiConfigChildProducts
          ? {
              multiConfigChildProducts: stampChildren(
                line.multiConfigChildProducts,
              ),
            }
          : {}),
      })),
    };
  };

  const stamped = Object.fromEntries(
    Object.entries(orders).map(([orderKey, order]) => [
      orderKey,
      stampOrder(order, new Set((groupPhotoIds?.[orderKey] || []).map(String))),
    ]),
  ) as T;
  return changed ? stamped : orders;
};
